import type { SiteBuilderStyleRecord } from "./siteBuilderStyles";
import type { SiteBuilderBlock } from "./siteBuilderTypes";

export const WEB_FOOTER_LAYOUT_VARIANTS = [
    "editorial",
    "columns",
    "centered",
    "compact",
    "split",
    "stacked",
    "showcase",
    "minimal",
] as const;

export const WEB_FOOTER_LAYOUT_SKELETON_VERSION = "1";

export type WebFooterVariant = typeof WEB_FOOTER_LAYOUT_VARIANTS[number];
export type LegacyWebFooterVariant = "contact" | "simple";

export type WebFooterLayoutRole =
    | "brand"
    | "navigation"
    | "support"
    | "social"
    | "divider"
    | "legal";

export type WebFooterGridPlacement = {
    h: number;
    w: number;
    x: number;
    y: number;
};

export const WEB_FOOTER_EXTENDED_SLOT_KEYS = [
    "aboveDivider",
    "belowDivider",
] as const;

export type WebFooterExtendedSlotKey =
    typeof WEB_FOOTER_EXTENDED_SLOT_KEYS[number];

export type WebFooterExtendedSlotState = Readonly<{
    aboveDivider: boolean;
    belowDivider: boolean;
}>;

/**
 * The Web footer uses the same eight layout concepts as the shared builder,
 * but maps them onto Web document roles. Keeping the placements here makes a
 * variant a real layout change instead of a cosmetic select value.
 */
export const WEB_FOOTER_LAYOUT_PLACEMENTS = {
    editorial: {
        brand: { x: 1, y: 1, w: 4, h: 3 },
        navigation: { x: 6, y: 1, w: 2, h: 3 },
        support: { x: 8, y: 1, w: 2, h: 3 },
        social: { x: 10, y: 1, w: 3, h: 3 },
        divider: { x: 1, y: 4, w: 12, h: 1 },
        legal: { x: 1, y: 5, w: 12, h: 1 },
    },
    columns: {
        brand: { x: 1, y: 1, w: 3, h: 3 },
        navigation: { x: 4, y: 1, w: 3, h: 3 },
        support: { x: 7, y: 1, w: 3, h: 3 },
        social: { x: 10, y: 1, w: 3, h: 3 },
        divider: { x: 1, y: 4, w: 12, h: 1 },
        legal: { x: 1, y: 5, w: 12, h: 1 },
    },
    centered: {
        brand: { x: 3, y: 1, w: 8, h: 2 },
        navigation: { x: 2, y: 3, w: 3, h: 2 },
        support: { x: 5, y: 3, w: 3, h: 2 },
        social: { x: 8, y: 3, w: 3, h: 2 },
        divider: { x: 1, y: 5, w: 12, h: 1 },
        legal: { x: 3, y: 6, w: 8, h: 1 },
    },
    compact: {
        brand: { x: 1, y: 1, w: 4, h: 2 },
        navigation: { x: 5, y: 1, w: 2, h: 2 },
        support: { x: 7, y: 1, w: 3, h: 2 },
        social: { x: 10, y: 1, w: 3, h: 2 },
        divider: { x: 1, y: 3, w: 12, h: 1 },
        legal: { x: 1, y: 4, w: 12, h: 1 },
    },
    split: {
        brand: { x: 1, y: 1, w: 6, h: 4 },
        navigation: { x: 7, y: 1, w: 3, h: 2 },
        support: { x: 10, y: 1, w: 3, h: 2 },
        social: { x: 7, y: 3, w: 6, h: 2 },
        divider: { x: 1, y: 5, w: 12, h: 1 },
        legal: { x: 1, y: 6, w: 12, h: 1 },
    },
    stacked: {
        brand: { x: 1, y: 1, w: 8, h: 2 },
        navigation: { x: 1, y: 4, w: 6, h: 2 },
        support: { x: 7, y: 4, w: 6, h: 2 },
        social: { x: 9, y: 1, w: 4, h: 2 },
        divider: { x: 1, y: 3, w: 12, h: 1 },
        legal: { x: 1, y: 6, w: 12, h: 1 },
    },
    showcase: {
        brand: { x: 1, y: 1, w: 12, h: 2 },
        navigation: { x: 1, y: 3, w: 3, h: 3 },
        support: { x: 4, y: 3, w: 3, h: 3 },
        social: { x: 8, y: 3, w: 5, h: 3 },
        divider: { x: 1, y: 6, w: 12, h: 1 },
        legal: { x: 1, y: 7, w: 12, h: 1 },
    },
    minimal: {
        brand: { x: 1, y: 1, w: 5, h: 2 },
        navigation: { x: 6, y: 1, w: 2, h: 2 },
        support: { x: 8, y: 1, w: 2, h: 2 },
        social: { x: 10, y: 1, w: 3, h: 2 },
        divider: { x: 1, y: 3, w: 12, h: 1 },
        legal: { x: 1, y: 4, w: 12, h: 1 },
    },
} as const satisfies Record<
    WebFooterVariant,
    Record<WebFooterLayoutRole, WebFooterGridPlacement>
>;

export const WEB_FOOTER_LAYOUT_ROOT_OPTIONS = {
    editorial: { minHeight: "290px", paddingBottom: "46px", paddingTop: "46px" },
    columns: { minHeight: "280px", paddingBottom: "42px", paddingTop: "42px" },
    centered: { minHeight: "340px", paddingBottom: "50px", paddingTop: "50px" },
    compact: { minHeight: "220px", paddingBottom: "30px", paddingTop: "30px" },
    split: { minHeight: "330px", paddingBottom: "48px", paddingTop: "48px" },
    stacked: { minHeight: "340px", paddingBottom: "48px", paddingTop: "48px" },
    showcase: { minHeight: "390px", paddingBottom: "54px", paddingTop: "54px" },
    minimal: { minHeight: "210px", paddingBottom: "28px", paddingTop: "28px" },
} as const satisfies Record<
    WebFooterVariant,
    Readonly<{
        minHeight: string;
        paddingBottom: string;
        paddingTop: string;
    }>
>;

const WEB_FOOTER_LAYOUT_ROLES = [
    "brand",
    "navigation",
    "support",
    "social",
    "divider",
    "legal",
] as const satisfies readonly WebFooterLayoutRole[];

const WEB_FOOTER_CANONICAL_DOCUMENT_ROLES = {
    brand: "footerBrand",
    navigation: "footerNavigation",
    support: "footerSupport",
    social: "footerSocial",
    divider: "footerDivider",
    legal: "footerLegal",
} as const satisfies Record<WebFooterLayoutRole, string>;

const FOOTER_ROLE_ALIASES: Readonly<Record<string, WebFooterLayoutRole>> = {
    footerBrand: "brand",
    footerCopy: "brand",
    footerCatalog: "navigation",
    footerNavigation: "navigation",
    footerContact: "support",
    footerPages: "support",
    footerSupport: "support",
    footerSocial: "social",
    footerDivider: "divider",
    footerBottom: "legal",
    footerCopyright: "legal",
    footerLegal: "legal",
};

function option(block: SiteBuilderBlock, key: string): unknown {
    return block.options?.[key];
}

function optionText(block: SiteBuilderBlock, key: string): string {
    const value = option(block, key);

    return typeof value === "string" ? value : "";
}

function numberValue(value: unknown, fallback: number): number {
    const numeric = typeof value === "number" ? value : Number(value);

    return Number.isFinite(numeric) ? numeric : fallback;
}

function blocksInSlot(block: SiteBuilderBlock, key: string): SiteBuilderBlock[] {
    return Array.isArray(block.slots?.[key]) ? block.slots?.[key] ?? [] : [];
}

function hasStoredGridPlacement(block: SiteBuilderBlock): boolean {
    return ["gridX", "gridY", "gridW", "gridH"]
        .some((key) => option(block, key) !== undefined);
}

function storedGridItemStyle(block: SiteBuilderBlock): SiteBuilderStyleRecord {
    if (!hasStoredGridPlacement(block)) {
        return {};
    }

    const gridX = Math.max(1, numberValue(option(block, "gridX"), 1));
    const gridY = Math.max(1, numberValue(option(block, "gridY"), 1));
    const gridW = Math.max(1, numberValue(option(block, "gridW"), 12));
    const gridH = Math.max(1, numberValue(option(block, "gridH"), 1));

    return {
        gridColumn: `${gridX} / span ${gridW}`,
        gridRow: `${gridY} / span ${gridH}`,
        minWidth: 0,
    };
}

function inferredFooterRole(block: SiteBuilderBlock): WebFooterLayoutRole | null {
    const storedRole = optionText(block, "documentRole");
    const aliasedRole = FOOTER_ROLE_ALIASES[storedRole];

    if (aliasedRole) {
        return aliasedRole;
    }

    const content = [
        block,
        ...blocksInSlot(block, "content"),
        ...(block.children ?? []),
    ];

    if (content.some((child) => child.type === "separator")) {
        return "divider";
    }

    if (content.some((child) => {
        const text = typeof child.content?.text === "string"
            ? child.content.text.trim()
            : "";

        return optionText(child, "runtimeText") === "copyright"
            || text.startsWith("©");
    })) {
        return "legal";
    }

    return null;
}

export function normalizeWebFooterVariant(
    value: unknown,
    fallback: unknown = "columns",
): WebFooterVariant {
    if (value === "contact") {
        return "split";
    }

    if (value === "simple") {
        return "minimal";
    }

    return typeof value === "string"
        && (WEB_FOOTER_LAYOUT_VARIANTS as readonly string[]).includes(value)
        ? value as WebFooterVariant
        : value !== fallback
            ? normalizeWebFooterVariant(fallback)
            : "columns";
}

function footerRoleAlignment(
    variant: WebFooterVariant,
    role: WebFooterLayoutRole,
): "center" | "left" | "right" {
    const centered = variant === "centered" || variant === "showcase";

    if (role === "social") {
        if (variant === "columns" || variant === "editorial") {
            return "left";
        }

        return centered || variant === "split" ? "center" : "right";
    }

    return centered ? "center" : "left";
}

function patchNestedFooterAlignment(
    block: SiteBuilderBlock,
    variant: WebFooterVariant,
    parentRole: WebFooterLayoutRole,
): SiteBuilderBlock {
    const alignment = footerRoleAlignment(variant, parentRole);
    const options = {
        ...(block.options ?? {}),
        ...(block.type === "socialMedia"
            ? { socialAlignment: alignment }
            : {}),
    };
    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(
            ([slotKey, children]) => [
                slotKey,
                children.map((child) => patchNestedFooterAlignment(
                    child,
                    variant,
                    parentRole,
                )),
            ],
        ))
        : undefined;
    const children = block.children?.map((child) => (
        patchNestedFooterAlignment(child, variant, parentRole)
    ));

    return {
        ...block,
        ...(block.options || block.type === "socialMedia" ? { options } : {}),
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };
}

function patchFooterRoleLayout(
    block: SiteBuilderBlock,
    variant: WebFooterVariant,
    role: WebFooterLayoutRole,
): SiteBuilderBlock {
    const placement = WEB_FOOTER_LAYOUT_PLACEMENTS[variant][role];
    const options: Record<string, unknown> = {
        ...(block.options ?? {}),
        documentRole: WEB_FOOTER_CANONICAL_DOCUMENT_ROLES[role],
        gridH: placement.h,
        gridW: placement.w,
        gridX: placement.x,
        gridY: placement.y,
    };

    if (role !== "divider") {
        options.alignment = footerRoleAlignment(variant, role);
        options.verticalAlign = (
            variant === "compact"
            || variant === "minimal"
            || role === "legal"
        ) ? "center" : "top";
    }

    if (role === "legal") {
        options.contentLayout = "inline";
    }

    return patchNestedFooterAlignment(
        {
            ...block,
            options,
        },
        variant,
        role,
    );
}

function extractedFooterSocial(
    block: SiteBuilderBlock,
): {
    block: SiteBuilderBlock;
    social: SiteBuilderBlock[];
} {
    const content = blocksInSlot(block, "content");
    const social = content.filter((child) => child.type === "socialMedia");

    if (social.length === 0) {
        return { block, social };
    }

    return {
        block: {
            ...block,
            slots: {
                ...(block.slots ?? {}),
                content: content.filter((child) => child.type !== "socialMedia"),
            },
        },
        social,
    };
}

/**
 * Materializes the same semantic footer skeleton that the variant picker
 * depicts. Existing role content, custom blocks and child UUIDs are preserved;
 * the legacy Restaurant social block is promoted from the brand column so it
 * can occupy the dedicated social cell used by every canonical layout.
 */
export function applyWebFooterLayoutSkeleton(
    footer: SiteBuilderBlock,
    value: WebFooterVariant | LegacyWebFooterVariant,
): SiteBuilderBlock {
    if (footer.type !== "footer" && footer.type !== "richContent") {
        return footer;
    }

    const variant = normalizeWebFooterVariant(value);
    const currentItems = blocksInSlot(footer, "grid");
    const workingItems = [...currentItems];
    const existingSocialIndex = workingItems.findIndex(
        (item) => inferredFooterRole(item) === "social",
    );

    if (existingSocialIndex < 0) {
        const brandIndex = workingItems.findIndex(
            (item) => inferredFooterRole(item) === "brand",
        );

        if (brandIndex >= 0 && workingItems[brandIndex]) {
            const extracted = extractedFooterSocial(workingItems[brandIndex]);

            workingItems[brandIndex] = extracted.block;

            if (extracted.social.length > 0) {
                workingItems.push({
                    type: "column",
                    options: {
                        documentRole: WEB_FOOTER_CANONICAL_DOCUMENT_ROLES.social,
                    },
                    slots: {
                        content: extracted.social,
                    },
                });
            }
        }
    }

    const usedIndexes = new Set<number>();
    const roleItems = WEB_FOOTER_LAYOUT_ROLES.flatMap((role) => {
        const index = workingItems.findIndex((item, candidateIndex) => (
            !usedIndexes.has(candidateIndex)
            && inferredFooterRole(item) === role
        ));

        if (index < 0 || !workingItems[index]) {
            return [];
        }

        usedIndexes.add(index);

        return [patchFooterRoleLayout(workingItems[index], variant, role)];
    });
    const customItems = workingItems.filter(
        (_, index) => !usedIndexes.has(index),
    );
    const next: SiteBuilderBlock = {
        ...footer,
        type: "footer",
        options: {
            ...(footer.options ?? {}),
            ...WEB_FOOTER_LAYOUT_ROOT_OPTIONS[variant],
            footerVariant: variant,
            webFooterLayoutSkeletonVariant: variant,
            webFooterLayoutSkeletonVersion:
                WEB_FOOTER_LAYOUT_SKELETON_VERSION,
        },
        slots: {
            ...(footer.slots ?? {}),
            grid: [...roleItems, ...customItems],
        },
    };

    return JSON.stringify(next) === JSON.stringify(footer) ? footer : next;
}

export function webFooterUsesLayoutSkeleton(
    block: SiteBuilderBlock,
): boolean {
    return optionText(
        block,
        "webFooterLayoutSkeletonVersion",
    ) === WEB_FOOTER_LAYOUT_SKELETON_VERSION;
}

function placementGridItemStyle(
    placement: WebFooterGridPlacement,
): SiteBuilderStyleRecord {
    return {
        gridColumn: `${placement.x} / span ${placement.w}`,
        gridRow: placement.h === 1
            ? `${placement.y}`
            : `${placement.y} / span ${placement.h}`,
        minWidth: 0,
    };
}

function shiftedFooterGridItemStyle(
    style: SiteBuilderStyleRecord,
    variant: WebFooterVariant,
    extendedSlots: Partial<WebFooterExtendedSlotState>,
): SiteBuilderStyleRecord {
    const value = String(style.gridRow ?? "").trim();
    const match = value.match(/^(\d+)(?:\s*\/\s*span\s*(\d+))?$/u);

    if (!match?.[1]) {
        return style;
    }

    const row = Number(match[1]);
    const span = Number(match[2] ?? 1);
    const dividerRow = WEB_FOOTER_LAYOUT_PLACEMENTS[variant].divider.y;
    const offset = (
        extendedSlots.aboveDivider && row >= dividerRow ? 1 : 0
    ) + (
        extendedSlots.belowDivider && row > dividerRow ? 1 : 0
    );

    if (offset === 0) {
        return style;
    }

    return {
        ...style,
        gridRow: span === 1
            ? `${row + offset}`
            : `${row + offset} / span ${span}`,
    };
}

export function webFooterExtendedSlotGridItemStyle(
    variant: WebFooterVariant | LegacyWebFooterVariant,
    slot: WebFooterExtendedSlotKey,
    extendedSlots: Partial<WebFooterExtendedSlotState>,
): SiteBuilderStyleRecord {
    const normalizedVariant = normalizeWebFooterVariant(variant);
    const divider = WEB_FOOTER_LAYOUT_PLACEMENTS[
        normalizedVariant
    ].divider;
    const row = slot === "aboveDivider"
        ? divider.y
        : divider.y + divider.h + (
            extendedSlots.aboveDivider ? 1 : 0
        );

    return {
        gridColumn: "1 / -1",
        gridRow: `${row}`,
        minWidth: 0,
    };
}

function firstFallbackRow(variant: WebFooterVariant): number {
    return Math.max(...Object.values(WEB_FOOTER_LAYOUT_PLACEMENTS[variant])
        .map((placement) => placement.y + placement.h - 1)) + 1;
}

function unassignedFooterItemStyle(
    block: SiteBuilderBlock,
    index: number,
    variant: WebFooterVariant,
): SiteBuilderStyleRecord {
    const storedPlacement = storedGridItemStyle(block);

    return {
        gridColumn: storedPlacement.gridColumn ?? "1 / -1",
        // Custom blocks remain visible and cannot overlap a canonical role or
        // another custom block. Their stored horizontal size is still useful.
        gridRow: `${firstFallbackRow(variant) + Math.max(0, index)}`,
        minWidth: 0,
    };
}

export function resolveWebFooterVariant(
    block: SiteBuilderBlock,
): WebFooterVariant {
    return normalizeWebFooterVariant(optionText(block, "footerVariant"));
}

export function siteBuilderBlockStacksOnMobile(
    block: SiteBuilderBlock,
): boolean {
    const value = option(block, "stackOnMobile");

    return value !== false
        && value !== "false"
        && value !== 0
        && value !== "0";
}

export function webFooterFallbackIndex(
    blocks: SiteBuilderBlock[],
    index: number,
): number {
    return blocks
        .slice(0, Math.max(0, index))
        .filter((block) => inferredFooterRole(block) === null)
        .length;
}

export function webFooterGridItemStyle(
    block: SiteBuilderBlock,
    variant: WebFooterVariant | LegacyWebFooterVariant,
    fallbackIndex: number,
    preferStoredPlacement = false,
    extendedSlots: Partial<WebFooterExtendedSlotState> = {},
): SiteBuilderStyleRecord {
    const normalizedVariant = normalizeWebFooterVariant(variant);
    const role = inferredFooterRole(block);

    if (role === null) {
        if (preferStoredPlacement && hasStoredGridPlacement(block)) {
            return shiftedFooterGridItemStyle(
                storedGridItemStyle(block),
                normalizedVariant,
                extendedSlots,
            );
        }

        return shiftedFooterGridItemStyle(
            unassignedFooterItemStyle(
                block,
                fallbackIndex,
                normalizedVariant,
            ),
            normalizedVariant,
            extendedSlots,
        );
    }

    if (
        (preferStoredPlacement || normalizedVariant === "columns")
        && hasStoredGridPlacement(block)
    ) {
        return shiftedFooterGridItemStyle(
            storedGridItemStyle(block),
            normalizedVariant,
            extendedSlots,
        );
    }

    return shiftedFooterGridItemStyle(
        placementGridItemStyle(
            WEB_FOOTER_LAYOUT_PLACEMENTS[normalizedVariant][role],
        ),
        normalizedVariant,
        extendedSlots,
    );
}

export function webFooterGridItemPresentationStyle(
    block: SiteBuilderBlock,
    variant: WebFooterVariant | LegacyWebFooterVariant,
): SiteBuilderStyleRecord {
    const normalizedVariant = normalizeWebFooterVariant(variant);
    const role = inferredFooterRole(block);
    const highlighted = (
        normalizedVariant === "split"
        && role === "brand"
    ) || (
        normalizedVariant === "showcase"
        && role === "social"
    );

    if (!highlighted) {
        return {};
    }

    return {
        alignSelf: "stretch",
        backgroundColor: "color-mix(in srgb, currentColor 6%, transparent)",
        borderColor: "color-mix(in srgb, currentColor 12%, transparent)",
        borderRadius: "var(--site-radius, 16px)",
        borderStyle: "solid",
        borderWidth: "1px",
        padding: "clamp(1rem, 2.4vw, 2rem)",
    };
}

function gridRowEnd(style: SiteBuilderStyleRecord): number {
    const value = String(style.gridRow ?? "").trim();
    const match = value.match(/^(\d+)(?:\s*\/\s*span\s*(\d+))?$/u);

    if (!match?.[1]) {
        return 1;
    }

    const start = Number(match[1]);
    const span = Number(match[2] ?? 1);

    return start + span - 1;
}

export function webFooterGridRowCount(
    blocks: SiteBuilderBlock[],
    variant: WebFooterVariant | LegacyWebFooterVariant,
    preferStoredPlacement = false,
    extendedSlots: Partial<WebFooterExtendedSlotState> = {},
): number {
    const normalizedVariant = normalizeWebFooterVariant(variant);
    const extendedSlotRows = WEB_FOOTER_EXTENDED_SLOT_KEYS.flatMap(
        (slot) => extendedSlots[slot]
            ? [gridRowEnd(webFooterExtendedSlotGridItemStyle(
                normalizedVariant,
                slot,
                extendedSlots,
            ))]
            : [],
    );

    return Math.max(
        1,
        ...extendedSlotRows,
        ...blocks.map((block, index) => gridRowEnd(
            webFooterGridItemStyle(
                block,
                normalizedVariant,
                webFooterFallbackIndex(blocks, index),
                preferStoredPlacement,
                extendedSlots,
            ),
        )),
    );
}
